import React, { useCallback, useEffect, useState } from 'react';
import { Button, SparkModalPortal, Spinner, sparkToast } from '@spark';
import ModelOutputStep from '@/features/model-setup/components/ModelOutputStep';
import ResponseTypeSelector from '@/features/model-setup/components/ResponseTypeSelector';
import ResponseParserInspectLogicModal from '@/features/model-setup/components/ResponseParserInspectLogicModal';
import ValidationAlert from '@/features/model-setup/components/ValidationAlert';
import { getValuesByPath } from '@/features/model-setup/components/utils';
import { useParserCodeState } from '@/features/model-setup/hooks/useParserCodeState';
import { useResponseMappingState } from '@/features/model-setup/hooks/useResponseMappingState';
import { data_from_server_post } from '@/shared/http/client';
// Step-card / JSON-viewer / response-type styles used by ModelOutputStep.
import '@/features/model-setup/components/ModelSetupPage.css';

// SparkModalPortal is z-index 1300 (SparkModalPortal.css). The nested antd
// Inspect-logic modal must sit above it or it opens behind Model Returns.
export const INSPECT_LOGIC_MODAL_Z_INDEX = 1400;

/**
 * Playground "Inspect" modal (Figma node 1905:134999): a "What the model
 * returns" card seeded with the clicked output cell's raw text. Reuses the
 * Model Configuration parser components — header via ResponseStepCard
 * (title / subtitle / Inspect logic, no badge, no Run), ResponseTypeSelector,
 * ModelOutputStep's raw viewer + task-type mapping summary — plus a Save
 * footer that persists the parser via the same `/validate-code` flow.
 *
 * On a valid save, `onSaved(parserFunction)` fires so the Playground can
 * refresh its usecase copy and re-parse the existing raw outputs.
 */

const PlaygroundInspectModal = ({ open, usecase, rawText, onClose, onSaved }) => {
    const taskType = usecase?.task_type;
    const isMultilabel = usecase?.is_multilabel;

    const [responseType, setResponseType] = useState('json');
    const [rawResponse, setRawResponse] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [logicModalOpen, setLogicModalOpen] = useState(false);

    // Seed from the clicked cell each time the modal opens. Closing Model
    // Returns must also dismiss Inspect logic so it cannot linger on its own.
    useEffect(() => {
        if (open) {
            setRawResponse(rawText || '');
            setValidationResult(null);
        } else {
            setLogicModalOpen(false);
        }
    }, [open, rawText]);

    const {
        userCode,
        updateUserCode,
        handleParserPrefillChange,
        parserSelectionsFromCode,
        prefillAutoCodeIfUntouched,
        handleKeyDown,
        getFullCode,
    } = useParserCodeState({ usecase, setResponseType });

    const mapping = useResponseMappingState({
        taskType,
        isMultilabel,
        responseType,
        setResponseType,
        rawResponse,
        initialParserSelections: parserSelectionsFromCode,
        onParserPrefillChange: handleParserPrefillChange,
        onUserCodeChange: updateUserCode,
    });

    // Same persistence path as Model Configuration: `/validate-code` saves
    // the parser function on the usecase when the code is valid.
    const runValidation = useCallback((onValid) => {
        const fullCode = getFullCode();
        setValidationLoading(true);
        setValidationResult(null);
        data_from_server_post(
            'validate-code',
            {
                code: fullCode,
                raw_response: rawResponse,
                task_type: taskType,
                client_id: usecase?.client_id,
                llm_usecase_id: usecase?._id,
            },
            (result) => {
                setValidationLoading(false);
                if (result.detail) {
                    sparkToast.error(result.detail);
                    setValidationResult({ is_valid: false, error_message: result.detail });
                    return;
                }
                setValidationResult(result);
                if (result.is_valid && onValid) {
                    onValid(fullCode);
                }
            },
            (errorMessage) => {
                setValidationLoading(false);
                setValidationResult({ is_valid: false, error_message: errorMessage });
            }
        );
    }, [getFullCode, rawResponse, taskType, usecase?.client_id, usecase?._id]);

    const handleSave = useCallback(() => {
        runValidation((fullCode) => {
            onSaved?.(fullCode);
            onClose();
        });
    }, [runValidation, onSaved, onClose]);

    const handleInspectLogic = useCallback(() => {
        prefillAutoCodeIfUntouched();
        setLogicModalOpen(true);
    }, [prefillAutoCodeIfUntouched]);

    // Custom parser code has no dedicated modal design — show the raw output
    // as plain text; the code itself is edited through "Inspect logic".
    const displayResponseType = responseType === 'custom' ? 'text' : responseType;

    return (
        <>
            <SparkModalPortal
                open={open}
                onClose={onClose}
                className="pg-inspect-modal-portal"
                focusTrap={!logicModalOpen}
                keyboard={!logicModalOpen}
            >
                <div className="pg-inspect-modal fs-unmask">
                    <ModelOutputStep
                        hideStepBadge
                        hideRunButton
                        onInspectLogic={handleInspectLogic}
                        responseType={displayResponseType}
                        rawResponse={rawResponse}
                        jsonParseError={mapping.jsonParseError}
                        parsedJson={mapping.parsedJson}
                        taskType={taskType}
                        isMultilabel={isMultilabel}
                        showHighlightInstruction={mapping.showHighlightInstruction}
                        beforeContent={(
                            <ResponseTypeSelector
                                taskType={taskType}
                                responseType={responseType}
                                onChange={mapping.handleResponseTypeChange}
                                showJsonOption={mapping.showJsonResponseTypeOption}
                            />
                        )}
                        // JSON Viewer props
                        isNerJsonMode={mapping.isNerJsonMode}
                        selectedJsonTokens={mapping.selectedJsonTokens}
                        nerPathAssignments={mapping.nerPathAssignments}
                        toggleJsonSelection={mapping.toggleJsonSelection}
                        handleRemoveNerAssignment={mapping.handleRemoveNerAssignment}
                        buildNerDropdownMenu={mapping.buildNerDropdownMenu}
                        jsonFieldMetaMap={mapping.jsonFieldMetaMap}
                        getLabelFromFieldPath={mapping.getLabelFromFieldPath}
                        // Classification / summarization summary props
                        normalizedJsonLabelField={mapping.normalizedJsonLabelField}
                        jsonLabelFieldOptions={mapping.jsonLabelFieldOptions}
                        setSelectedJsonLabelField={mapping.handleLabelFieldChange}
                        multilabelDelimiter={mapping.multilabelDelimiter}
                        setMultilabelDelimiter={mapping.setMultilabelDelimiter}
                        singleLabelListMode={mapping.singleLabelListMode}
                        setSingleLabelListMode={mapping.setSingleLabelListMode}
                        getJsonValueByPath={mapping.getJsonValueByPath}
                        formatNerAssignedValue={mapping.formatNerAssignedValue}
                        splitLabels={mapping.splitLabels}
                        delimiterOptions={mapping.delimiterOptions}
                        getValuesByPath={getValuesByPath}
                        initialParserSelections={parserSelectionsFromCode}
                        // NER summary props
                        handleManualNerAssignment={mapping.handleManualNerAssignment}
                        jsonFieldSelectOptions={mapping.jsonFieldSelectOptions}
                        entityFieldConfig={mapping.entityFieldConfig}
                        relationFieldConfig={mapping.relationFieldConfig}
                        supportsRelationFields={mapping.supportsRelationFields}
                        nerFieldAssignments={mapping.nerFieldAssignments}
                    />
                    {validationResult && !validationResult.is_valid && (
                        <div className="pg-inspect-modal__validation">
                            <ValidationAlert
                                validationResult={validationResult}
                                taskType={taskType}
                                isMultilabel={isMultilabel}
                            />
                        </div>
                    )}
                    <div className="pg-inspect-modal__footer">
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            startIcon={validationLoading ? <Spinner size="small" /> : undefined}
                            disabled={validationLoading}
                            disableWhenReadOnly
                            onClick={handleSave}
                            className="fs-unmask"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </SparkModalPortal>
            <ResponseParserInspectLogicModal
                open={logicModalOpen}
                onClose={() => setLogicModalOpen(false)}
                taskType={taskType}
                isMultilabel={isMultilabel}
                userCode={userCode}
                onCodeChange={updateUserCode}
                handleKeyDown={handleKeyDown}
                validationResult={validationResult}
                validationLoading={validationLoading}
                onValidate={() => runValidation()}
                teacher={usecase?.teacher_model}
                zIndex={INSPECT_LOGIC_MODAL_Z_INDEX}
            />
        </>
    );
};

export default PlaygroundInspectModal;
